import React, { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useUser } from '../context/UserProvider';
import PropTypes from 'prop-types';
import LoadingScreen from './LoadingScreen';
import { useApi } from '../context/ApiProvider';
import { onAuthStateChange } from '../firebase';

function PrivateRoute({ children }) {
  const api = useApi();
  const { user: kipUser, setUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (user) => {
      if (user) {
        setFirebaseUser(user);
        const token = await user.getIdToken();
        localStorage.setItem('accessToken', token);
        await handleFirebaseUser(user);
      } else {
        setFirebaseUser(null);
        if (kipUser?.auth_provider === 'firebase') {
          setUser(null);
          localStorage.removeItem('accessToken');
        }
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [kipUser?.auth_provider]);

  async function createFirebaseUser(user) {
    setLoading(true);
    const userInfo = {
      firebase_uid: user.uid,
      username: user.email,
      email: user.email,
      about_me: user.displayName || '',
    };

    try {
      const signupResult = await api.post('/users', userInfo);
      if (signupResult.ok) {
        const userObj = signupResult.body;
        setUser(userObj);
      } else {
        console.error(
          'Error creating/updating user:',
          signupResult.body?.errors?.json
        );
      }
    } catch (error) {
      console.error('Error in createFirebaseUser:', error);
    } finally {
      setLoading(false);
    }
  }

  function getProviderData(user) {
    if (user?.providerData?.length > 0) {
      return user.providerData[0].providerId.replace('.com', '');
    }
    return null;
  }

  async function handleFirebaseUser(user) {
    const existingUserResponse = await api.get(`/users/${user.email}`);

    if (existingUserResponse.ok) {
      const existingUser = existingUserResponse.body;
      if (existingUser.firebase_uid !== user.uid) {
        const updateUserResponse = await api.put('/users/auth-id', {
          id: String(existingUser.id),
          firebase_uid: user.uid,
          auth_provider: getProviderData(user),
        });
        if (updateUserResponse.ok) {
          setUser(updateUserResponse.body);
        } else {
          console.error(
            'Error updating user:',
            updateUserResponse.body?.errors?.json
          );
        }
      } else {
        setUser(existingUserResponse.body);
      }
    } else {
      await createFirebaseUser(user);
    }
  }

  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (firebaseUser || kipUser) {
    return children;
  }

  const url = location.pathname + location.search + location.hash;
  localStorage.setItem('next', url);
  return <Navigate to="/login" state={{ next: url }} />;
}

PrivateRoute.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default PrivateRoute;
